// Module-level singleton — stream continues in background when user navigates away.
// Listeners (components) subscribe/unsubscribe without aborting the stream.
import { freshToken, refreshAccess } from './client';
import { userIdFromToken } from '../lib/authToken';

function getCacheKey() {
  const uid = userIdFromToken();
  return uid ? `agence_insights_${uid}` : 'agence_insights';
}

// Singleton state
let _insights = null;     // null = not started; Array = in progress or done
let _done = false;        // true when stream completed
let _error = null;        // Error if stream failed permanently
let _controller = null;   // AbortController for active stream
const _listeners = new Set();

function resetStream() {
  if (_controller) { _controller.abort(); _controller = null; }
  _insights = null;
  _done = false;
  _error = null;
  _listeners.clear();
  try { sessionStorage.removeItem(getCacheKey()); } catch {}
}

export function invalidateInsightsCache() {
  resetStream();
}

/**
 * Synchronous cache read — returns cached insights array or null.
 * Safe to call in useState/useRef initializers. Has the side effect of
 * hydrating module state from sessionStorage if needed (idempotent).
 */
export function getInsightsCacheSync() {
  if (_done && _insights !== null) return _insights;
  try {
    const raw = sessionStorage.getItem(getCacheKey());
    if (raw) {
      const { insights: saved } = JSON.parse(raw);
      if (Array.isArray(saved)) {
        _insights = saved;
        _done = true;
        return saved;
      }
    }
  } catch {}
  return null;
}

const BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5000/api/v1');

function startStream(force = false) {
  _controller = new AbortController();

  const streamURL = `${BASE_URL}/insights/stream${force ? '?force=true' : ''}`;

  async function fetchStream(t) {
    return fetch(streamURL, {
      headers: { Authorization: `Bearer ${t}` },
      signal: _controller.signal,
    });
  }

  (async () => {
    try {
      let token = await freshToken();
      let res = await fetchStream(token);

      // Token missing or expired — attempt one silent refresh then retry.
      // fetch() bypasses the axios interceptor, so we use refreshAccess() which
      // deduplicates concurrent refresh calls (e.g. AuthContext startup + this).
      if (res.status === 401) {
        try {
          token = await refreshAccess();
        } catch {
          throw new Error('HTTP 401');
        }
        res = await fetchStream(token);
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const rawEvent of parts) {
          if (!rawEvent.trim()) continue;
          let eventType = '';
          let dataStr = '';
          for (const line of rawEvent.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
          }
          if (!dataStr) continue;
          try {
            const payload = JSON.parse(dataStr);
            if (eventType === 'insight') {
              _insights.push(payload);
              for (const l of _listeners) if (l.onInsight) l.onInsight(payload);
            }
            if (eventType === 'done') {
              _done = true;
              _controller = null;
              try {
                sessionStorage.setItem(getCacheKey(), JSON.stringify({ insights: _insights }));
              } catch {}
              for (const l of [..._listeners]) if (l.onDone) l.onDone(payload);
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        _error = err;
        _controller = null;
        for (const l of [..._listeners]) if (l.onError) l.onError(err);
        _listeners.clear();
      }
    }
  })();
}

/**
 * Stream insights. On first call, starts the SSE stream; on subsequent calls
 * (e.g. navigating back to Insights), delivers accumulated insights immediately
 * and subscribes for any remaining ones. Navigating away just removes the listener —
 * the stream continues in the background so returning is instant.
 *
 * Returns a cancel function — call on component unmount.
 *
 * @param {Object} opts
 * @param {Function} opts.onInsight
 * @param {Function} opts.onDone
 * @param {Function} opts.onError
 * @param {boolean}  opts.force - bypass cache and start fresh stream
 */
export function streamInsights({ onInsight, onDone, onError, force = false } = {}) {
  if (force) resetStream();

  // Hydrate from sessionStorage if in-memory state was lost (e.g. page refresh)
  if (_insights === null) {
    try {
      const raw = sessionStorage.getItem(getCacheKey());
      if (raw) {
        const { insights: saved } = JSON.parse(raw);
        if (Array.isArray(saved)) {
          _insights = saved;
          _done = true;
        }
      }
    } catch {}
  }

  // Completed — deliver from memory
  if (_done && _insights !== null) {
    const snapshot = _insights.slice();
    Promise.resolve().then(() => {
      for (const ins of snapshot) if (onInsight) onInsight(ins);
      if (onDone) onDone({ total: snapshot.length });
    });
    return () => {};
  }

  // Permanent error — propagate
  if (_error !== null) {
    const err = _error;
    Promise.resolve().then(() => { if (onError) onError(err); });
    return () => {};
  }

  const listener = { onInsight, onDone, onError };

  // Stream already in progress — deliver accumulated so far, then subscribe
  if (_insights !== null) {
    const snapshotLen = _insights.length;
    _listeners.add(listener);
    Promise.resolve().then(() => {
      for (let i = 0; i < snapshotLen; i++) if (onInsight) onInsight(_insights[i]);
    });
    return () => _listeners.delete(listener);
  }

  // Start fresh stream
  _insights = [];
  _listeners.add(listener);
  startStream(force);
  return () => _listeners.delete(listener);
}

/**
 * Returns cached insights synchronously from sessionStorage or in-memory state,
 * or falls back to a REST fetch. Used by Dashboard for the top-3 insight preview.
 */
export async function getCachedInsights() {
  // In-memory (navigation within session)
  if (_done && _insights !== null) return _insights;

  // sessionStorage (page refresh)
  try {
    const raw = sessionStorage.getItem(getCacheKey());
    if (raw) {
      const { insights: saved } = JSON.parse(raw);
      if (Array.isArray(saved)) return saved;
    }
  } catch {}

  // Fallback: REST fetch (no streaming on Dashboard)
  const BASE = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5000/api/v1');
  let fbToken = await freshToken();
  let res = await fetch(`${BASE}/insights`, {
    headers: { Authorization: `Bearer ${fbToken}` },
  });
  if (res.status === 401) {
    try {
      fbToken = await refreshAccess();
    } catch {
      throw new Error('HTTP 401');
    }
    res = await fetch(`${BASE}/insights`, { headers: { Authorization: `Bearer ${fbToken}` } });
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.insights || [];
}
